import { cache } from './cache';

const API_ENV = process.env.REACT_APP_API_ENV || '';

/**
 * Fetch JSON data, optionally prefixing the request with a base URL.
 *
 * In test environments this first checks sessionStorage-backed cache by request path.
 * If a valid cached response exists, it is returned immediately and no network request is made.
 * Otherwise the request is sent, non-2xx responses throw, and successful JSON responses are cached.
 *
 * @param path Request path or full URL when no baseUrl is provided.
 * @param baseUrl Optional base URL prepended to the path.
 */
export const fetcher = async <T>(path: string, baseUrl = ''): Promise<T> => {
  const cacheKey = path;
  const url = baseUrl ? `${baseUrl}/${path}` : path;

  // In test env, return cached data immediately when it is still valid.
  if (API_ENV.indexOf('test') > -1) {
    const cached = cache.isValid(cacheKey);
    if (cached.isValid) {
      return JSON.parse(cached.value || '{}') as T;
    }
  }

  // No valid cache entry was found, so continue with the network request.
  const response = await fetch(url);

  // Mirror axios-style failure handling by rejecting non-2xx responses.
  if (!response.ok) {
    throw new Error(`Request failed with status ${response.status}`);
  }

  const data = (await response.json()) as T;

  // Store fresh successful responses in test env for future requests.
  if (API_ENV.indexOf('test') > -1) {
    cache.store(cacheKey, JSON.stringify(data));
  }

  return data;
};
